import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { Map, fromJS } from 'immutable'
import { StyleElement } from '../../Main/StyleElement'
import { formatStyleText } from '../../../constants/addStyleClasses'
import { useLangDirection } from '../../../hooks/useLangDirection'
import { getPersistentDrawerIsCut } from '../../../lib/processPortalRules'
import { IS_SERVER } from '../../../constants/ssr'
import { useLayoutMode } from '../../../hooks/useLayoutMode'

function ElementStyleComponent({ uid, pageID, mode, stripRoot, isPreview }) {
  const isRTL = useLangDirection()
  const footerHeight = useSelector((state) =>
    state.getIn(['project', 'Settings', 'theme', 'lg', 'Footer', 'root', 'height'], '')
  )
  const PowerTableField = useSelector((state) =>
    state.getIn(['project', 'Settings', 'theme', 'lg', 'PowerTableField'], Map())
  )
  let style = useSelector((state) =>
    state.getIn(
      pageID === 'project' ? ['project', 'Settings', 'styles', uid] : ['pages', pageID, 'Props', 'styles', uid],
      Map()
    )
  )
  const IsConfiguredDevice = useSelector((state) =>
    state.getIn(['pages', state.get('page'), 'Props', 'IsConfiguredDevice'], Map())
  )
  const styleText = useMemo(() => {
    let useStyle = style
    IsConfiguredDevice.map((isConfigured, key) => {
      if (isConfigured && !useStyle.get(key)) {
        useStyle = useStyle.setIn(
          [key],
          fromJS({
            root: {},
          })
        )
      }
    })
    if (uid === 'Footer' || useStyle.get('type') === 'Footer') {
      if (!useStyle.getIn([mode, 'root', 'height'])) {
        useStyle = useStyle.setIn([mode, 'root', 'height'], footerHeight)
      }
      if (!useStyle.getIn([mode, 'row0'])) {
        useStyle = useStyle.setIn([mode, 'row0'], footerHeight)
        useStyle = useStyle.setIn([mode, 'row0Unit'], 'px')
      }
    }
    if (useStyle.get('type') === 'PowerTable') {
      useStyle = useStyle.map((value, key) => {
        if (['lg', 'md', 'sm', 'xs', 'ss'].includes(key)) {
          return value.setIn(['mui'], PowerTableField.mergeDeep(value.getIn(['mui'], Map())))
        } else {
          return value
        }
      })
    }
    return (
      formatStyleText(`ft--${uid}`, useStyle, mode, stripRoot, uid, isPreview, isRTL) +
      ` .ft--component-wrap.ft--pos--${uid}, .ft--component-wrap.ft--${uid}--root { opacity: unset; }`
    )
  }, [style, mode, stripRoot, isPreview, isRTL, IsConfiguredDevice, footerHeight, uid, PowerTableField])

  return <StyleElement key={uid} uid={uid} style={styleText} />
}

export function ElementStyleChunk({ chunk, pageID, mode, stripRoot, isPreview }) {
  const isRTL = useLangDirection()
  const layoutMode = useLayoutMode()
  const footerHeight = useSelector((state) =>
    state.getIn(['project', 'Settings', 'theme', 'lg', 'Footer', 'root', 'height'], '')
  )
  const PowerTableField = useSelector((state) =>
    state.getIn(['project', 'Settings', 'theme', 'lg', 'PowerTableField'], Map())
  )
  const isCutStrip = useSelector(getPersistentDrawerIsCut)
  const defaultColors = useSelector((state) =>
    state.getIn(['project', 'Settings', 'theme', 'lg', 'DateRange', 'cColors'], Map())
  )
  const projectPageID = useSelector((state) => state.get('page'))
  let styles = useSelector(
    (state) =>
      _.reduce(
        chunk,
        (r, uid) => {
          return {
            ...r,
            [uid]: state.getIn(
              pageID === 'project' ? ['project', 'Settings', 'styles', uid] : ['pages', pageID, 'Props', 'styles', uid],
              Map()
            ),
          }
        },
        {}
      ),
    shallowEqual
  )
  const IsConfiguredDevice = useSelector((state) =>
    state.getIn(['pages', state.get('page'), 'Props', 'IsConfiguredDevice'], Map())
  )
  const [styleText, setStyleText] = useState(() => {
    if (pageID !== 'project' && IS_SERVER) {
      return ''
    }
    let text = []
    IsConfiguredDevice.map((isConfigured, key) => {
      _.each(styles, (style) => {
        if (isConfigured && !style.get(key)) {
          style = style.setIn(
            [key],
            fromJS({
              root: {},
            })
          )
        }
      })
    })

    _.each(styles, (style, uid) => {
      if (style.get('type') === 'PowerTable') {
        style = style.map((value, key) => {
          if (['lg', 'md', 'sm', 'xs', 'ss'].includes(key)) {
            return value.setIn(['mui'], PowerTableField.mergeDeep(value.getIn(['mui'], Map())))
          } else {
            return value
          }
        })
      }
      if (uid === 'Footer' || style.get('type') === 'Footer') {
        if (!style.getIn([mode, 'root', 'height'])) {
          style = style.setIn([mode, 'root', 'height'], footerHeight)
        }
        if (!style.getIn([mode, 'row0'])) {
          style = style.setIn([mode, 'row0'], footerHeight)
          style = style.setIn([mode, 'row0Unit'], 'px')
        }
      } else if (style.get('type') === 'DateRange') {
        style = style.setIn([mode, 'cColors'], defaultColors.merge(style.getIn([mode, 'cColors'], Map())))
      }
      let styleText = formatStyleText(`ft--${uid}`, style, mode, stripRoot, uid, isPreview, isRTL, isCutStrip)
      styleText += ` .ft--component-wrap.ft--pos--${uid}, .ft--component-wrap.ft--${uid}--root { opacity: unset; }`
      text.push(styleText)
    })
    return text.join('')
  })

  useEffect(() => {
    let allowApply = true
    IsConfiguredDevice.map((isConfigured, key) => {
      _.each(styles, (style) => {
        if (isConfigured && !style.get(key)) {
          style = style.setIn(
            [key],
            fromJS({
              root: {},
            })
          )
        }
      })
    })
    let text = []
    if (typeof window?.__FT__BUILDER_API__?.workerTask === 'function') {
      Promise.all(
        _.map(styles, async (style, uid) => {
          if (style.get('type') === 'PowerTable') {
            style = style.map((value, key) => {
              if (['lg', 'md', 'sm', 'xs', 'ss'].includes(key)) {
                return value.setIn(['mui'], PowerTableField.mergeDeep(value.getIn(['mui'], Map())))
              } else {
                return value
              }
            })
          }
          if (uid === 'Footer' || style.get('type') === 'Footer') {
            if (!style.getIn([mode, 'root', 'height'])) {
              style = style.setIn([mode, 'root', 'height'], footerHeight)
            }
            if (!style.getIn([mode, 'row0'])) {
              style = style.setIn([mode, 'row0'], footerHeight)
              style = style.setIn([mode, 'row0Unit'], 'px')
            }
          } else if (style.get('type') === 'DateRange') {
            style = style.setIn([mode, 'cColors'], defaultColors.merge(style.getIn([mode, 'cColors'], Map())))
          }
          if (allowApply) {
            let styleText = await window?.__FT__BUILDER_API__?.workerTask(
              'style',
              `ft--${uid}`,
              style,
              mode,
              stripRoot,
              uid,
              isPreview,
              isRTL,
              isCutStrip
            )
            if (!styleText) {
              styleText = formatStyleText(`ft--${uid}`, style, mode, stripRoot, uid, isPreview, isRTL, isCutStrip)
            }
            styleText += ` .ft--component-wrap.ft--pos--${uid}, .ft--component-wrap.ft--${uid}--root { opacity: unset; }`
            text.push(styleText)
          }
        })
      )
        .then(() => {
          if (allowApply && text.length > 0) {
            setStyleText(text.join(''))
          }
        })
        .catch((error) => {
          console.error(error)
          if (allowApply) {
            _.each(styles, (style, uid) => {
              if (uid === 'Footer' || style.get('type') === 'Footer') {
                if (!style.getIn([mode, 'root', 'height'])) {
                  style = style.setIn([mode, 'root', 'height'], footerHeight)
                }
                if (!style.getIn([mode, 'row0'])) {
                  style = style.setIn([mode, 'row0'], footerHeight)
                  style = style.setIn([mode, 'row0Unit'], 'px')
                }
              } else if (style.get('type') === 'DateRange') {
                style = style.setIn([mode, 'cColors'], defaultColors.merge(style.getIn([mode, 'cColors'], Map())))
              }
              let styleText = formatStyleText(`ft--${uid}`, style, mode, stripRoot, uid, isPreview, isRTL, isCutStrip)
              styleText += ` .ft--component-wrap.ft--pos--${uid}, .ft--component-wrap.ft--${uid}--root { opacity: unset; }`
              text.push(styleText)
            })
            setStyleText(text.join(''))
          }
        })
    } else {
      let text = []
      _.each(styles, (style, uid) => {
        if (style.get('type') === 'PowerTable') {
          style = style.map((value, key) => {
            if (['lg', 'md', 'sm', 'xs', 'ss'].includes(key)) {
              return value.setIn(['mui'], PowerTableField.mergeDeep(value.getIn(['mui'], Map())))
            } else {
              return value
            }
          })
        }
        if (uid === 'Footer' || style.get('type') === 'Footer') {
          if (!style.getIn([mode, 'root', 'height'])) {
            style = style.setIn([mode, 'root', 'height'], footerHeight)
          }
          if (!style.getIn([mode, 'row0'])) {
            style = style.setIn([mode, 'row0'], footerHeight)
            style = style.setIn([mode, 'row0Unit'], 'px')
          }
        } else if (style.get('type') === 'DateRange') {
          style = style.setIn([mode, 'cColors'], defaultColors.merge(style.getIn([mode, 'cColors'], Map())))
        }
        let styleText = formatStyleText(`ft--${uid}`, style, mode, stripRoot, uid, isPreview, isRTL, isCutStrip)
        styleText += ` .ft--component-wrap.ft--pos--${uid}, .ft--component-wrap.ft--${uid}--root { opacity: unset; }`
        text.push(styleText)
      })
      setStyleText(text.join(''))
    }
    return () => {
      allowApply = false
    }
  }, [
    styles,
    mode,
    stripRoot,
    isPreview,
    isRTL,
    IsConfiguredDevice,
    defaultColors,
    isCutStrip,
    projectPageID,
    footerHeight,
    PowerTableField,
  ])

  return (
    <StyleElement
      key={`${projectPageID}${pageID}${chunk.join('_')}${layoutMode}`}
      uid={`${pageID}${chunk.join('_')}`}
      style={styleText}
    />
  )
}

export const ElementStyle = React.memo(ElementStyleComponent)
